import { useMemo } from "react";
import { HomePeopleRawNormDataItem } from "@api/useHomePeopleRawNormData";
import { TableColumn } from "@components/Table/TableColumns";
import { getRem } from "@datassential/platform-ui-lib";
import { ALL_RESPONDENT_TYPES } from "@models/RespondentType";
import { useHomeContext } from "@pages/Home/HomeContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { useGetRespondentRawTableCell } from "@vms/TableCells/useGetRespondentRawTableCell";
import { COMMON_NUM_COL_WIDTH_PX } from "@core/constants";
import { useGetEmptyTableCells } from "@vms/TableCells/useGetEmptyTableCells";

export type PeopleViewIndexTableConfigProps = {
    data: HomePeopleRawNormDataItem[];
}

export function usePeopleViewIndexTableConfig({
    data,
}: PeopleViewIndexTableConfigProps) {
    const { search } = useHomeContext();
    const { data: { respondentTypes } } = useSelectAmongContext();
    const trimmedSearch = useMemo(() => search.trim(), [search])
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const selectedRespondentTypesIds = useMemo(
        () => ALL_RESPONDENT_TYPES.filter(i => respondentTypes.includes(i)),
        [respondentTypes]
    );

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const rawColumn = useGetRespondentRawTableCell(data);
    const getEmptyCells = useGetEmptyTableCells();

    const colWidth = useMemo(
        () => getRem(COMMON_NUM_COL_WIDTH_PX * 11 / selectedRespondentTypesIds.length),
        [selectedRespondentTypesIds.length]
    );

    const paddingCols = useMemo(
        () => {
            if (selectedRespondentTypesIds.length === 0) {
                return getEmptyCells(11);
            }

            return [];
        },
        [selectedRespondentTypesIds, getEmptyCells]
    );

    return useMemo(
        (): TableColumn[] => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
                style: {
                    ...nameColumn.style,
                    cell: {
                        ...nameColumn.style?.cell,
                        flex: `1 0 ${getRem(200)}`,
                    }
                },
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            ...selectedRespondentTypesIds.flatMap((id) => {
                const raw = rawColumn(id, 'index');
                return {
                    ...raw,
                    sortKey: hideSorting ? undefined : `column${id}`,
                    isHiddenForTextMode: true,
                    style: {
                        ...raw.style,
                        cell: {
                            ...raw.style?.cell,
                            flex: `0 0 ${colWidth}`,
                        },
                    },
                };
            }),
            {
                key: 'index-copy',
                head: 'Index',
                Value: () => null,
                isHiddenForRender: true,
                subColumns: ALL_RESPONDENT_TYPES.map(
                    (id) => rawColumn(id, 'index')
                ),
            },
            ...paddingCols,
        ],
        [
            nameColumn,
            macStageColumn,
            rawColumn,
            selectedRespondentTypesIds,
            hideSorting,
            paddingCols,
            colWidth
        ]
    );
}